import './style.css';
import { useEffect, useState } from 'react';
import CircuitTable from 'components/circuit-table';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createModal, LoadingIndicator, Tooltip } from '@thermon-platforms/thermon-ui-core';
import { ConfigureDeviceModal, EditView } from '../manage-devices';
import { Link, useLocation } from 'react-router-dom';
import { getConfigItem } from 'util/device';
import useAlarmsStore from 'store/alarms';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import DevicesMap from '../devices-map';
import Actions from '../actions';
import StatusIcon from 'components/status-icon';
import CircuitsGroupChart from 'components/charts/circuits-group-chart';
import CardManagementButton from '../card-management';
import ImagesButton from '../images';
import { NotesModal } from 'components/notes';
import LockOutButton from 'components/lockout-button';


function ControllerView({device}) {
  const location = useLocation();
  const [detail, setDetail] = useState();
  const [status, setStatus] = useState();
  const [loading, setLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [alarmsStore] = useAlarmsStore();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [deviceTypesStore] = useDeviceTypesStore();
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();
  const [alarms, setAlarms] = useState([]);
  const [editMode, setEditMode] = useState(location.hash == '#network-settings');
  const LOTO = getConfigItem(device.model, 'LockoutTagOut');
  const type = deviceTypesStore.types.find(t => t.id == device.model.deviceModel);
  const canEdit = usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles);

  // Edit mode can be navigated directly to via a hash in the url
  // Let's watch for the location to change and set edit mode
  // appropriately
  useEffect(() => {
    setEditMode(location.hash == '#network-settings');
  }, [location]);

  useEffect(() => {
    setLoading(true);

    devicesStoreActions.fetchDetail(device.model.id).then((detail) => {
      setDetail(detail);
      setLoading(false);
    });

    devicesStoreActions.fetchStatus(device.model.id).then((status) => {
      setStatus(status);
    });
  }, [device]);

  function editSettings() {
    setSettingsLoading(true);
    devicesStoreActions.fetchDetail(device.model.id).then((res) => {
      setSettingsLoading(false);
      setDetail(res);
      const editModal = createModal(<ConfigureDeviceModal device={res} />);
      editModal.open()
        .then((res) => {
          // Handle side-effects
          devicesStoreActions.forceUpdate();
        })
        .catch(() => {/* no-op */});
    });
  }

  function viewNotes() {
    const notesModal = createModal(<NotesModal device={device?.model} />);
    notesModal.open()
      .then(() => {
        // Handle side-effects
      })
      .catch(() => {/* no-op */});
  }

  useEffect(() => {
    if (!alarmsStore.alarms.length) {
      return;
    }

    // Check for alarm status on this device
    setAlarms(alarmsStore.alarms.filter(alarm => {
      if (!alarm.deviceId) return false;
      // If alarm is on this device add it
      if (alarm.deviceId == device.model.id) return true;
      // If this device is in the path of the device with the alarm add it
      const path = devicesStore.shallowDevices.get(alarm.deviceId)?.path.map(d => d.id);
      if (!path) return false;
      return path.includes(device.model.id);
    }));
  }, [alarmsStore]);

  if (editMode) return (<EditView device={device} />);

  return (
    <>
    <header>
      <h2>
        {loading ?
          <LoadingIndicator /> :
          <StatusIcon device={device} alarms={alarms} />
        }
        {device.model.name}
      </h2>
      <ul className="device-controls">
        {canEdit ? <>
          <li>
            <button
              className={`settings ${settingsLoading ? 'processing' : ''}`}
              onClick={editSettings}
              data-tooltip-content={t`Controller Settings`}
              data-tooltip-id="control-tooltip"
              disabled={loading || settingsLoading}
            >
              <Trans>Settings</Trans>
            </button>
          </li>
          <li>
            <Link
              to={'#network-settings'}
              className="button network"
              data-tooltip-content={t`Network Settings`}
              data-tooltip-id="control-tooltip"
              onClick={() => setEditMode(true)}
            >
              <Trans>Network Settings</Trans>
            </Link>
          </li>
          {/* <li>
            <LockOutButton device={device} disabled={loading} />
          </li> */}
        </> : null}
        <li>
          <button
            className="note"
            data-tooltip-content={t`View Notes`}
            data-tooltip-id="control-tooltip"
            onClick={viewNotes}
          >
            <Trans>Notes</Trans>
          </button>
        </li>
        <li>
          <ImagesButton device={device} />
        </li>
        <Actions wrapper={true} device={device.model} />
        {type?.name == 'T2000' && usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <li>
          <CardManagementButton
            device={device}
            onClose={() => {
              devicesStoreActions.fetchDetail(device.model.id).then((detail) => {
                setDetail(detail);
              });
            }}
          />
        </li> : null}
      </ul>
      <Tooltip id="control-tooltip" place="bottom-end" />
    </header>
    <section>
      <div className="overview">
        <ul>
          <li>
            <strong><Trans>Status</Trans></strong>: {LOTO?.value == 'on' ? 'Locked Out' : status?.commStatus }
          </li>
          {status && (('modbusAddress' in status && status.modbusAddress !== null && status.modbusAddress !== undefined) ||
                      ('canbusAddress' in status && status.canbusAddress !== null && status.canbusAddress !== undefined)) ? <li>
            <strong>
              {('canbusAddress' in status && status.canbusAddress !== null && status.canbusAddress !== undefined) ? <Trans>CANbus Address</Trans> : <Trans>Modbus Address</Trans>}
            </strong>
              : {('canbusAddress' in status && status.canbusAddress !== null && status.canbusAddress !== undefined) ? status?.canbusAddress : status?.modbusAddress}
          </li> : null}
          {detail?.firmwareVersion ?
            <li>
              <strong><Trans>Firmware Vesion</Trans></strong>:&nbsp;
              {detail?.firmwareVersion}
            </li>
          : <></>}
          {detail?.firmwareUpdatedAt ?
            <li>
              <strong><Trans>Firmware Updated</Trans></strong>:&nbsp;
              {detail?.firmwareUpdatedAt}
            </li>
          : <></>}
          <li><strong><Trans>Model</Trans></strong>: {type?.name}</li>
        </ul>
        <DevicesMap device={device} />
      </div>
      <CircuitTable device={device} />
      <br />
      <CircuitsGroupChart
        circuitIds={device.model.children.map(c => c.id)}
        hideCurrent={type?.name == 'SteamController'}
      />
    </section>
  </>);
}

export default ControllerView;
